import { useCallback, useEffect, useRef, useState } from "react";

import { messageOf } from "./errors";

/**
 * 서버를 부르는 동작 하나의 진행·오류 상태.
 *
 * 화면마다 `busy` 를 손으로 만들다 빠뜨리면 버튼이 응답 대기 중에도 눌려 **같은 요청이 두 번** 갑니다
 * (서비스 계정은 이름 유일성 제약이 없어 그대로 두 개가 생깁니다). 그래서 한 곳에 모읍니다.
 *
 *  - 진행 중 재호출은 **ref 로 즉시** 막습니다 — `pending` 상태는 다음 렌더에야 반영되므로, 같은
 *    틱의 더블클릭은 상태만으로는 막지 못합니다.
 *  - 실패는 던지지 않고 `error` 에 문구로 담고 `false` 를 돌려줍니다. 호출부는 성공일 때만 닫습니다 —
 *    실패에 모달을 닫으면 사용자가 입력을 다시 해야 합니다.
 *  - 언마운트 뒤 도착한 응답은 상태를 건드리지 않습니다.
 */
export function useAction() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runningRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    if (runningRef.current) return false;
    runningRef.current = true;
    setPending(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err: unknown) {
      if (aliveRef.current) setError(messageOf(err));
      return false;
    } finally {
      runningRef.current = false;
      if (aliveRef.current) setPending(false);
    }
  }, []);

  return { pending, error, setError, run };
}
